// SETTINGS V2 — Notifikácie data constants (SETTINGS_V2_SP5 §2 / §4.3 / §9.1).
//
// The 5 category buckets, their EXACT accent hexes, and their EXACT eventType
// arrays are BINDING (ticket §4.4). These hexes are CATEGORY-IDENTITY data
// constants — identical in both palettes — and are the ONE sanctioned hardcoded
// -hex exception in v2. Every OTHER color in the v2 chrome resolves via var(--…).
//
// Ported VERBATIM from the legacy customer/settings/components/sections/
// SettingsNotifications.tsx. The ONLY behavioral change vs legacy is the two
// DISTINCT event labels for 28 (ServiceReportUpdated) and 305
// (PartnershipDeactivated) per Q-SETV2-2 = B — the legacy map points both at
// duplicate keys; here they get their own keys (§9.1). Everything else is identical.
//
// Pure data + one pure function; no imports beyond the model types.
import {
  NotificationPreference,
  UpdateNotificationPreference,
} from "../../../../shared/notifications/models/notificationModels";

export type NotifCategoryKey =
  | "serviceOffers"
  | "serviceConfirmation"
  | "serviceExecution"
  | "partnerships"
  | "operations";

export interface NotifCategoryDef {
  key: NotifCategoryKey;
  titleKey: string;
  descKey: string;
  accent: string; // category-identity hex (sanctioned literal)
  accentSoft: string; // soft rgba of the accent (ported; kept for parity)
  eventTypes: number[];
}

// Event type → i18n key. 28 + 305 carry DISTINCT keys (Q-SETV2-2 = B); every
// other entry is byte-identical to the legacy map. Unknown/future event types
// degrade to the raw fallback key at the call site (§5.4 / acceptance 17).
export const eventTypeLabels: Record<number, string> = {
  1: "settings:notifications.eventTypes.newOfferReceived",
  2: "settings:notifications.eventTypes.offerViewed",
  3: "settings:notifications.eventTypes.offerAccepted",
  4: "settings:notifications.eventTypes.offerRejected",
  5: "settings:notifications.eventTypes.offerExpiringSoon",
  6: "settings:notifications.eventTypes.offerWithdrawn",
  7: "settings:notifications.eventTypes.providerConfirmationRequested",
  8: "settings:notifications.eventTypes.providerConfirmedService",
  9: "settings:notifications.eventTypes.confirmationReminder",
  10: "settings:notifications.eventTypes.confirmationOverdue",
  11: "settings:notifications.eventTypes.pastDeadlineWarning",
  20: "settings:notifications.eventTypes.workStarted",
  21: "settings:notifications.eventTypes.progressUpdatePosted",
  22: "settings:notifications.eventTypes.workCompleted",
  23: "settings:notifications.eventTypes.completionConfirmed",
  24: "settings:notifications.eventTypes.revisionRequested",
  25: "settings:notifications.eventTypes.reviewReceived",
  28: "settings:notifications.eventTypes.serviceReportUpdated",
  300: "settings:notifications.eventTypes.partnerInvitationReceived",
  301: "settings:notifications.eventTypes.partnerInvitationAccepted",
  302: "settings:notifications.eventTypes.partnerInvitationDeclined",
  305: "settings:notifications.eventTypes.partnershipDeactivated",
  307: "settings:notifications.eventTypes.privateRfsReceived",
  40: "settings:notifications.eventTypes.workOrderCreated",
  41: "settings:notifications.eventTypes.workOrderAssigned",
  42: "settings:notifications.eventTypes.workOrderStatusChanged",
  43: "settings:notifications.eventTypes.workOrderCompleted",
  44: "settings:notifications.eventTypes.workOrderOverdue",
  45: "settings:notifications.eventTypes.workOrderComment",
  46: "settings:notifications.eventTypes.recurringWorkOrderGenerated",
};

// 5 buckets · EXACT hexes + EXACT eventType arrays (30 event types total).
export const NOTIF_CATEGORIES: NotifCategoryDef[] = [
  {
    key: "serviceOffers",
    titleKey: "settings:notifications.categories.serviceOffers",
    descKey: "settings:notifications.categories.serviceOffersDesc",
    accent: "#0C1117",
    accentSoft: "rgba(12, 17, 23, 0.06)",
    eventTypes: [1, 2, 3, 4, 5, 6],
  },
  {
    key: "serviceConfirmation",
    titleKey: "settings:notifications.categories.serviceConfirmation",
    descKey: "settings:notifications.categories.serviceConfirmationDesc",
    accent: "#E8A817",
    accentSoft: "rgba(232, 168, 23, 0.08)",
    eventTypes: [7, 8, 9, 10, 11],
  },
  {
    key: "serviceExecution",
    titleKey: "settings:notifications.categories.serviceExecution",
    descKey: "settings:notifications.categories.serviceExecutionDesc",
    accent: "#3B82F6",
    accentSoft: "rgba(59, 130, 246, 0.08)",
    eventTypes: [20, 21, 22, 23, 24, 25, 28],
  },
  {
    key: "partnerships",
    titleKey: "settings:notifications.categories.partnerships",
    descKey: "settings:notifications.categories.partnershipsDesc",
    accent: "#2D8C4E",
    accentSoft: "rgba(45, 140, 78, 0.08)",
    eventTypes: [300, 301, 302, 305, 307],
  },
  {
    key: "operations",
    titleKey: "settings:notifications.categories.operations",
    descKey: "settings:notifications.categories.operationsDesc",
    accent: "#D4572B",
    accentSoft: "rgba(212, 87, 43, 0.08)",
    eventTypes: [40, 41, 42, 43, 44, 45, 46],
  },
];

// Whole-array PUT row builder — EXACTLY {eventType, isInAppEnabled, isEmailEnabled},
// NO id (parity §4.3). Ported verbatim from the legacy page.
export const toUpdatePayload = (
  p: NotificationPreference
): UpdateNotificationPreference => ({
  eventType: p.eventType,
  isInAppEnabled: p.isInAppEnabled,
  isEmailEnabled: p.isEmailEnabled,
});
